import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, AlertTriangle, PiggyBank, ShieldCheck, Download, Scale, FileSignature, CheckCircle2, XCircle, ChevronDown, Mail, CreditCard } from "lucide-react";
import LetterModal from "@/components/LetterModal";

export default function ReportView() {
  const [report, setReport] = useState(null);
  const [redundancy, setRedundancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [advice, setAdvice] = useState({}); // key: index -> {loading, data}
  const [openIdx, setOpenIdx] = useState(null);
  const [letter, setLetter] = useState({ open: false, loading: false, name: "", text: "" });

  const openLetter = async (adv, fallbackName) => {
    const name = adv?.cancel?.name || fallbackName;
    if (adv?.letter) {
      setLetter({ open: true, loading: false, name, text: adv.letter });
      return;
    }
    setLetter({ open: true, loading: true, name, text: "" });
    try {
      const { data } = await api.post("/cancellation/letter", { contract_name: name });
      setLetter({ open: true, loading: false, name: data.contract_name || name, text: data.letter });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de la génération de la lettre");
      setLetter({ open: false, loading: false, name: "", text: "" });
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/analysis/report");
        setReport(data);
      } finally {
        setLoading(false);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/analysis/card-redundancy");
        setRedundancy(data);
      } catch {
        setRedundancy(null);
      }
    })();
  }, []);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/report/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rapport-assurlens.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapport PDF téléchargé");
    } catch {
      toast.error("Échec de l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/report/email");
      toast.success(`Bilan envoyé à ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'envoi de l'email");
    } finally {
      setEmailing(false);
    }
  };

  const getAdvice = async (idx, item) => {
    if (openIdx === idx) { setOpenIdx(null); return; }
    setOpenIdx(idx);
    if (advice[idx]?.data) return;
    setAdvice((a) => ({ ...a, [idx]: { loading: true } }));
    try {
      const { data } = await api.post("/analysis/duplicate-advice", { title: item.title, detail: item.detail });
      setAdvice((a) => ({ ...a, [idx]: { loading: false, data } }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Erreur");
      setAdvice((a) => ({ ...a, [idx]: { loading: false } }));
      setOpenIdx(null);
    }
  };

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3 text-muted-foreground">
        <Loader2 className="w-7 h-7 animate-spin text-primary" />
        <p className="text-sm">Détection des doublons et failles…</p>
      </div>
    );

  const items = report?.items || [];
  const doublons = items.filter((i) => i.type === "doublon");
  const gaps = items.filter((i) => i.type === "gap");

  return (
    <div className="space-y-8 animate-in-up" data-testid="report-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Doublons & failles</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Là où vous payez double — ou pas assez</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="email-report-btn" onClick={emailReport} disabled={emailing} className="flex items-center gap-2 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition disabled:opacity-60">
            {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Recevoir par email
          </button>
          <button data-testid="export-pdf-btn" onClick={exportPdf} disabled={exporting} className="flex items-center gap-2 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition disabled:opacity-60">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exporter le rapport PDF
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-5">
        <div className="rounded-2xl border border-ochre/30 bg-accent text-accent-foreground p-6">
          <PiggyBank className="w-6 h-6" />
          <p className="font-heading text-3xl font-extrabold mt-3">{Math.round(report?.annual_savings ?? 0)} €</p>
          <p className="text-sm opacity-80 mt-0.5">Économisables par an</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <Copy className="w-6 h-6 text-ochre" />
          <p className="font-heading text-3xl font-extrabold mt-3">{doublons.length}</p>
          <p className="text-sm text-muted-foreground mt-0.5">Doublon{doublons.length > 1 ? "s" : ""} détecté{doublons.length > 1 ? "s" : ""}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <AlertTriangle className="w-6 h-6 text-destructive" />
          <p className="font-heading text-3xl font-extrabold mt-3">{gaps.length}</p>
          <p className="text-sm text-muted-foreground mt-0.5">Trou{gaps.length > 1 ? "s" : ""} dans la couverture</p>
        </div>
      </div>

      {redundancy?.items?.length > 0 && (
        <div data-testid="card-redundancy" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-600 grid place-items-center shrink-0">
              <CreditCard className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-heading font-bold text-lg leading-tight">Vos cartes couvrent déjà une assurance payante</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Ces assurances font potentiellement doublon avec une garantie de vos cartes bancaires
                {redundancy.total_annual_savings > 0 ? ` — jusqu'à ${Math.round(redundancy.total_annual_savings)} €/an économisables` : ""}.
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {redundancy.items.map((r, i) => (
              <div key={i} data-testid={`redundancy-item-${i}`} className="rounded-xl border border-emerald-500/30 bg-card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-heading font-bold">{r.insurance_name}</p>
                  {r.annual_savings > 0 && (
                    <span data-testid={`redundancy-savings-${i}`} className="text-sm font-semibold text-emerald-600 font-mono">≈ {Math.round(r.annual_savings)} €/an</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Déjà couvert par <span className="font-medium text-foreground">{r.card_name}</span> : {r.overlap}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Fiabilité {r.confidence}</span>
                  <button
                    data-testid={`redundancy-letter-${i}`}
                    onClick={() => openLetter(null, r.insurance_name)}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition"
                  >
                    <FileSignature className="w-4 h-4" /> Voir la lettre de résiliation
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Les garanties de carte sont soumises à conditions (achat réglé avec la carte, plafonds, durées). Vérifiez la notice de votre carte avant de résilier.
          </p>
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground text-sm flex flex-col items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-emerald-600" />
          Aucun doublon ni trou majeur détecté. Ajoutez des contrats pour affiner l'audit.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it, i) => {
            const isDup = it.type === "doublon";
            const adv = advice[i];
            return (
              <div key={i} data-testid={`report-item-${it.type}`} className={`rounded-2xl border overflow-hidden ${isDup ? "border-ochre/40 bg-ochre/5" : "border-destructive/40 bg-destructive/5"}`}>
                <div className="p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${isDup ? "bg-ochre/15 text-ochre" : "bg-destructive/15 text-destructive"}`}>
                      {isDup ? <Copy className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${isDup ? "bg-ochre/15 text-ochre" : "bg-destructive/15 text-destructive"}`}>
                          {isDup ? "Doublon" : "Trou / Manque"}
                        </span>
                        <p className="font-heading font-bold">{it.title}</p>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{it.detail}</p>
                      {isDup && it.savings > 0 && (
                        <p className="text-sm font-semibold text-ochre mt-3 font-mono">≈ {Math.round(it.savings)} €/an économisables</p>
                      )}
                      {isDup && (
                        <button
                          data-testid={`compare-cancel-btn-${i}`}
                          onClick={() => getAdvice(i, it)}
                          className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition"
                        >
                          <Scale className="w-4 h-4" /> Comparer & résilier <ChevronDown className={`w-4 h-4 transition ${openIdx === i ? "rotate-180" : ""}`} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {isDup && openIdx === i && (
                  <div className="border-t border-ochre/30 bg-card p-5 sm:p-6" data-testid={`advice-panel-${i}`}>
                    {adv?.loading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" /> Comparaison des contrats en cours…
                      </div>
                    ) : adv?.data ? (
                      <div className="space-y-5">
                        <div className="grid sm:grid-cols-2 gap-4">
                          {(adv.data.comparison || []).map((c, j) => {
                            const keep = c.role === "keep";
                            return (
                              <div key={j} className={`rounded-xl border p-4 ${keep ? "border-emerald-500/40 bg-emerald-500/5" : "border-destructive/40 bg-destructive/5"}`}>
                                <div className="flex items-center gap-2">
                                  {keep ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-destructive" />}
                                  <span className={`text-xs font-semibold uppercase tracking-wide ${keep ? "text-emerald-600" : "text-destructive"}`}>{keep ? "À garder" : "À résilier"}</span>
                                </div>
                                <p className="font-heading font-bold mt-2">{c.name}</p>
                                <ul className="mt-2 space-y-1 text-sm text-muted-foreground list-disc list-inside">
                                  {(c.points || []).map((p, k) => <li key={k}>{p}</li>)}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                        {adv.data.annual_savings > 0 && (
                          <p className="font-heading text-lg font-bold text-emerald-600">Résilier « {adv.data.cancel?.name} » vous ferait économiser ≈ {Math.round(adv.data.annual_savings)} €/an</p>
                        )}
                        <button
                          data-testid={`view-letter-btn-${i}`}
                          onClick={() => openLetter(adv.data, it.title)}
                          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition"
                        >
                          <FileSignature className="w-4 h-4" /> Voir la lettre de résiliation
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <LetterModal open={letter.open} loading={letter.loading} contractName={letter.name} letter={letter.text} onClose={() => setLetter({ open: false, loading: false, name: "", text: "" })} />
    </div>
  );
}
